import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { Order } from 'types/orders';
import type { PickupReturnsTabs } from 'components/Admin/organisms/Rentals/molecules/PickupReturns/constants';
import type { FilesDto } from 'store/slices/cars/types';

interface InitialState {
  items: Array<Order>;
  pendingOrders: Array<Order>;
  isPendingOrdersLoading: boolean;
  selectedOrder: Order | null;
  isPending: boolean;
  pickupReturnsTab: null | PickupReturnsTabs;
  pendingCommentId: string | null;
  pendingOrdersCount: number;
  totalCount: number;
  isRefundPending: boolean;
}

export type SecondaryDriver = {
  name: string;
  dateOfBirth: Date | null;
  licensePermitDate: Date | null;
  licenseNumber: string;
  country: string;
  city: string;
  email: string;
  passport: File | FilesDto | null;
  drivingLicense: File | FilesDto | null;
};

export type SecondaryDriverErrors = Record<keyof SecondaryDriver, boolean>;
const initialState: InitialState = {
  items: [],
  selectedOrder: null,
  isPending: false,
  pickupReturnsTab: null,
  pendingCommentId: null,
  pendingOrders: [],
  isPendingOrdersLoading: false,
  isRefundPending: false,
  pendingOrdersCount: 0,
  totalCount: 0,
};
export const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setRefundPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isRefundPending: action.payload,
      };
    },
    resetStore: (state) => {
      return {
        ...state,
        ...initialState,
      };
    },
    setPendingOrdersCount: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        pendingOrdersCount: action.payload,
      };
    },
    setIsPendingOrdersLoading: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isPendingOrdersLoading: action.payload,
      };
    },
    setPendingOrders: (state, action: PayloadAction<Array<Order>>) => {
      return {
        ...state,
        pendingOrders: action.payload,
      };
    },
    editMultipleOrders: (
      state,
      action: PayloadAction<Record<string, Order>>,
    ) => {
      return {
        ...state,
        items: state.items.map((item) =>
          action.payload[item.id] ? action.payload[item.id] : item,
        ),
      };
    },
    setPendingCommentId: (state, action: PayloadAction<string | null>) => {
      return {
        ...state,
        pendingCommentId: action.payload,
      };
    },
    updateOrder: (state, action: PayloadAction<Order>) => {
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item,
        ),
      };
    },
    removeSelectedOrderFile: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        selectedOrder: {
          ...(state.selectedOrder as Order),
          files: (state.selectedOrder as Order).files.filter(
            (item) => item.id !== action.payload,
          ),
        },
        items: state.items.map((item) =>
          item.id === state.selectedOrder?.id
            ? {
                ...item,
                files: (state.selectedOrder as Order).files.filter(
                  (item) => item.id !== action.payload,
                ),
              }
            : item,
        ),
      };
    },
    addNewOrderFile: (state, action: PayloadAction<FilesDto>) => {
      return {
        ...state,
        selectedOrder: {
          ...(state.selectedOrder as Order),
          files: (state.selectedOrder as Order).files.concat(action.payload),
        },
        items: state.items.map((item) =>
          item.id === state.selectedOrder?.id
            ? {
                ...item,
                files: item.files.concat(action.payload),
              }
            : item,
        ),
      };
    },
    removeOrder: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
      };
    },
    setPickupReturnsTab: (
      state,
      action: PayloadAction<null | PickupReturnsTabs>,
    ) => {
      return {
        ...state,
        pickupReturnsTab: action.payload,
      };
    },
    setSelectedOrder: (state, action: PayloadAction<Order | null>) => {
      return {
        ...state,
        selectedOrder: action.payload,
      };
    },
    setOrdersPendingStatus: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isPending: action.payload,
      };
    },
    setAllOrders: (state, action: PayloadAction<Array<Order>>) => {
      return {
        ...state,
        items: action.payload,
      };
    },
    setOrdersTotalCount: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        totalCount: action.payload,
      };
    },
    setNewOrder: (state, action: PayloadAction<Order>) => {
      return {
        ...state,
        items: state.items.concat(action.payload),
      };
    },
  },
});
